import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaTimes,
  FaDownload,
  FaFileInvoice,
  FaCheckSquare,
  FaRegSquare,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import { ReceiptIndianRupee } from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const VATReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(ThemeContext);

  const currentSkin =
    getCurrentSkin(skin);

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem("finYearId") || "";

  const finYear =
    localStorage.getItem("finYear") || "";

  const storedBranchId =
    localStorage.getItem("branchId") || "";

  const storedBranchType =
    localStorage.getItem("branchType") || "HO";

  const userId =
    localStorage.getItem("userId") || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid": finYearId,
        "x-finyear": finYear,
        "x-branchid": storedBranchId,
        "x-branch": storedBranchType,
        "x-userid": userId,
      }),
      [
        finYearId,
        finYear,
        storedBranchId,
        storedBranchType,
        userId,
      ]
    );

  /* =========================================================
     DATE
  ========================================================= */

  const today =
    new Date()
      .toISOString()
      .split("T")[0];

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    branchType,
    setBranchType,
  ] = useState(
    storedBranchType || "HO"
  );

  const [
    branchId,
    setBranchId,
  ] = useState(
    storedBranchId || "8"
  );

  const [
    fromDate,
    setFromDate,
  ] = useState(today);

  const [
    toDate,
    setToDate,
  ] = useState(today);

  const [
    isVatDisapproved,
    setIsVatDisapproved,
  ] = useState(false);

  /* =========================================================
     DATA
  ========================================================= */

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     PAGINATION
  ========================================================= */

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  const [
    rowsPerPage,
    setRowsPerPage,
  ] = useState(10);

  /* =========================================================
     TEMPLATE INPUT STYLES
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     FETCH BRANCHES
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/branches_VATrpt",
        {
          params: {
            branchtype:
              branchType,

            branchid:
              storedBranchId || 8,
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const branches =
          res.data?.data || [];

        setBranchList(branches);

        if (branches.length > 0) {
          setBranchId(
            String(
              branches[0]
                .fld_Branchid ||
                storedBranchId ||
                8
            )
          );
        }
      })
      .catch((err) => {
        console.error(
          "Branch fetch failed",
          err
        );

        toast.error(
          "Failed to load branches"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    branchType,
    storedBranchId,
    commonHeaders,
  ]);

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch = () => {
    if (
      !fromDate ||
      !toDate ||
      !branchId
    ) {
      toast.warning(
        "Please select From Date, To Date and Branch"
      );

      return;
    }

    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/vat_rpt",
        {
          params: {
            branchid:
              branchId,

            branchtype:
              branchType,

            isvatdis:
              isVatDisapproved,

            dtfrom:
              fromDate,

            dtto:
              toDate,
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const rows =
          res.data?.data || [];

        setData(rows);

        setCurrentPage(1);

        toast.success(
          `Loaded ${rows.length} records`
        );
      })
      .catch((err) => {
        console.error(
          "VAT listing fetch failed",
          err
        );

        toast.error(
          "Failed to fetch VAT listing"
        );

        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* =========================================================
     PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint = (
    fld_vat
  ) => {
    const query =
      new URLSearchParams({
        branchid:
          branchId,

        branchtype:
          branchType,

        isvatdis:
          isVatDisapproved,

        dtfrom:
          fromDate,

        dtto:
          toDate,

        fld_vat:
          fld_vat || "",
      }).toString();

    window.open(
      `https://nissi-india.com/api/vat_rpt_print?${query}`,
      "_blank"
    );
  };

  /* =========================================================
     EXPORT
     EXISTING FUNCTIONALITY PRESERVED
  ========================================================= */

  const handleExport = () => {
    toast.info(
      "Export API not connected yet"
    );
  };

  /* =========================================================
     CLEAR
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters = () => {
    setFromDate(today);

    setToDate(today);

    setIsVatDisapproved(false);

    setData([]);

    setCurrentPage(1);

    if (
      branchList.length > 0
    ) {
      setBranchId(
        String(
          branchList[0]
            .fld_Branchid ||
            storedBranchId ||
            8
        )
      );
    } else {
      setBranchId(
        storedBranchId ||
          "8"
      );
    }

    setBranchType(
      storedBranchType ||
        "HO"
    );
  };

  /* =========================================================
     CURRENCY
  ========================================================= */

  const formatCurrency = (
    amount
  ) => {
    return new Intl.NumberFormat(
      "en-IN",
      {
        style:
          "currency",

        currency:
          "INR",

        minimumFractionDigits:
          2,

        maximumFractionDigits:
          2,
      }
    ).format(
      Number(
        amount || 0
      )
    );
  };

  /* =========================================================
     TOTALS
  ========================================================= */

  const totals =
    useMemo(() => {
      return data.reduce(
        (
          acc,
          row
        ) => {
          acc.accAmount +=
            Number(
              row.AccAmount ||
                0
            );

          acc.vatAmount +=
            Number(
              row.VAT ||
                0
            );

          return acc;
        },
        {
          accAmount: 0,
          vatAmount: 0,
        }
      );
    }, [data]);

  /* =========================================================
     PAGINATION
  ========================================================= */

  const totalPages =
    Math.ceil(
      data.length /
        rowsPerPage
    );

  const paginatedData =
    useMemo(() => {
      const startIndex =
        (currentPage -
          1) *
        rowsPerPage;

      return data.slice(
        startIndex,
        startIndex +
          rowsPerPage
      );
    }, [
      data,
      currentPage,
      rowsPerPage,
    ]);

  const startRecord =
    data.length === 0
      ? 0
      : (currentPage -
          1) *
          rowsPerPage +
        1;

  const endRecord =
    Math.min(
      currentPage *
        rowsPerPage,
      data.length
    );

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            VAT Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <ReceiptIndianRupee className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  VAT Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Track and manage Value Added Tax records
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT */}

              <button
                type="button"
                onClick={
                  handleExport
                }
                disabled={
                  data.length ===
                  0
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FaDownload
                  size={14}
                />

                Export
              </button>

              {/* FILTER */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTER BODY
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">

                {/* FROM DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(e) =>
                      setFromDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* TO DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(e) =>
                      setToDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* BRANCH */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <select
                    value={
                      branchId
                    }
                    onChange={(e) =>
                      setBranchId(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    {branchList.map(
                      (b) => (
                        <option
                          key={
                            b.fld_Branchid
                          }
                          value={
                            b.fld_Branchid
                          }
                        >
                          {
                            b.fld_BranchName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* VAT STATUS */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    VAT Status
                  </label>

                  <button
                    type="button"
                    onClick={() =>
                      setIsVatDisapproved(
                        (prev) =>
                          !prev
                      )
                    }
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm font-medium transition ${
                      isVatDisapproved
                        ? `border-${baseColor}-500 bg-${baseColor}-50 text-${baseColor}-700`
                        : isDark
                        ? "bg-slate-800/60 border-slate-600/50 text-slate-300"
                        : "bg-white border-slate-300 text-slate-700"
                    }`}
                  >
                    <span>
                      VAT Disapproved
                    </span>

                    {isVatDisapproved ? (
                      <FaCheckSquare
                        className={`w-4 h-4 text-${baseColor}-600`}
                      />
                    ) : (
                      <FaRegSquare
                        className="w-4 h-4 text-slate-400"
                      />
                    )}
                  </button>
                </div>
              </div>

              {/* =============================================
                  ACTIONS
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  VAT Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} records
                </span>
              </div>

              {data.length > 0 && (
                <div className="flex flex-wrap items-center gap-4">

                  {/* TOTAL VAT */}

                  <div
                    className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                      isDark
                        ? "bg-green-900/30 text-green-300"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    Total VAT:{" "}
                    {formatCurrency(
                      totals.vatAmount
                    )}
                  </div>

                  {/* ROWS */}

                  <div className="flex items-center gap-2">

                    <label
                      className={`text-sm ${
                        isDark
                          ? "text-slate-400"
                          : "text-slate-500"
                      }`}
                    >
                      Rows:
                    </label>

                    <select
                      value={
                        rowsPerPage
                      }
                      onChange={(e) => {
                        setRowsPerPage(
                          Number(
                            e.target.value
                          )
                        );

                        setCurrentPage(1);
                      }}
                      className={`px-3 py-2 border rounded-lg text-sm ${
                        isDark
                          ? "bg-slate-800 border-slate-600 text-white"
                          : "bg-white border-slate-300 text-slate-800"
                      }`}
                    >
                      <option value={10}>
                        10
                      </option>

                      <option value={25}>
                        25
                      </option>

                      <option value={50}>
                        50
                      </option>

                      <option value={100}>
                        100
                      </option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading VAT records...
              </p>
            </div>
          ) : data.length > 0 ? (
            <>
              {/* =============================================
                  TABLE
              ============================================= */}

              <div className="overflow-x-auto">

                <table className="w-full text-sm">

                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        S.No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Branch
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Input VAT
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Accessible Amount
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        VAT Amount
                      </th>

                      <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`divide-y ${
                      isDark
                        ? "divide-gray-600"
                        : "divide-gray-200"
                    }`}
                  >
                    {paginatedData.map(
                      (
                        row,
                        index
                      ) => {
                        const serialNo =
                          (currentPage -
                            1) *
                            rowsPerPage +
                          index +
                          1;

                        return (
                          <tr
                            key={`${row.fld_branchname}-${row.fld_vat}-${serialNo}`}
                            className={`transition ${
                              isDark
                                ? "bg-gray-800/20 hover:bg-gray-700/30"
                                : "bg-white hover:bg-gray-50"
                            }`}
                          >

                            {/* SERIAL */}

                            <td className="px-4 py-3 font-medium">
                              {serialNo}
                            </td>

                            {/* BRANCH */}

                            <td className="px-4 py-3 font-medium">
                              {row.fld_branchname ||
                                "-"}
                            </td>

                            {/* INPUT VAT */}

                            <td className="px-4 py-3">

                              <span
                                className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                  isDark
                                    ? "bg-gray-700 text-gray-200"
                                    : `bg-${baseColor}-100 text-${baseColor}-800`
                                }`}
                              >
                                {row.fld_vat ||
                                  "-"}
                              </span>
                            </td>

                            {/* ACCESSIBLE AMOUNT */}

                            <td className="px-4 py-3 whitespace-nowrap font-medium">
                              {formatCurrency(
                                row.AccAmount
                              )}
                            </td>

                            {/* VAT */}

                            <td
                              className={`px-4 py-3 whitespace-nowrap font-semibold ${
                                isDark
                                  ? "text-green-300"
                                  : "text-green-700"
                              }`}
                            >
                              {formatCurrency(
                                row.VAT
                              )}
                            </td>

                            {/* PRINT */}

                            <td className="px-4 py-3 text-center">

                              <button
                                type="button"
                                onClick={() =>
                                  handlePrint(
                                    row.fld_vat
                                  )
                                }
                                className={`p-1.5 rounded-full transition ${
                                  isDark
                                    ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                                    : "bg-green-100 text-green-600 hover:bg-green-200"
                                }`}
                                title="Print Report"
                              >
                                <FaPrint className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>

                  {/* =========================================
                      TOTALS
                  ========================================= */}

                  <tfoot
                    className={
                      isDark
                        ? "bg-slate-900/70"
                        : "bg-slate-100"
                    }
                  >
                    <tr>

                      <td
                        colSpan="3"
                        className={`px-4 py-3 text-right text-sm font-bold ${
                          isDark
                            ? "text-white"
                            : "text-slate-900"
                        }`}
                      >
                        Totals
                      </td>

                      <td className="px-4 py-3 text-sm font-bold">
                        {formatCurrency(
                          totals.accAmount
                        )}
                      </td>

                      <td
                        className={`px-4 py-3 text-sm font-bold ${
                          isDark
                            ? "text-green-300"
                            : "text-green-700"
                        }`}
                      >
                        {formatCurrency(
                          totals.vatAmount
                        )}
                      </td>

                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* =============================================
                  PAGINATION
              ============================================= */}

              <div
                className={`p-6 border-t ${
                  isDark
                    ? "border-slate-700"
                    : "border-slate-200"
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                  {/* COUNT */}

                  <div
                    className={`text-sm ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-600"
                    }`}
                  >
                    Showing{" "}
                    <span className="font-semibold">
                      {startRecord}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold">
                      {endRecord}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold">
                      {data.length}
                    </span>{" "}
                    entries
                  </div>

                  {/* PAGINATION */}

                  <div className="flex items-center gap-2">

                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage(
                          (prev) =>
                            Math.max(
                              prev - 1,
                              1
                            )
                        )
                      }
                      disabled={
                        currentPage === 1
                      }
                      className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                        currentPage === 1
                          ? isDark
                            ? "border-slate-700 text-slate-600 cursor-not-allowed"
                            : "border-slate-200 text-slate-400 cursor-not-allowed"
                          : isDark
                          ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                          : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <FaChevronLeft
                        size={12}
                      />

                      Prev
                    </button>

                    <span
                      className={`px-4 py-2 text-sm font-semibold rounded-lg bg-${baseColor}-${baseShade} text-white`}
                    >
                      Page{" "}
                      {currentPage}{" "}
                      of{" "}
                      {totalPages || 1}
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage(
                          (prev) =>
                            Math.min(
                              prev + 1,
                              totalPages ||
                                1
                            )
                        )
                      }
                      disabled={
                        currentPage ===
                          totalPages ||
                        totalPages === 0
                      }
                      className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                        currentPage ===
                          totalPages ||
                        totalPages === 0
                          ? isDark
                            ? "border-slate-700 text-slate-600 cursor-not-allowed"
                            : "border-slate-200 text-slate-400 cursor-not-allowed"
                          : isDark
                          ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                          : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      Next

                      <FaChevronRight
                        size={12}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* ===============================================
               EMPTY
            =============================================== */

            <div className="px-4 py-14 text-center">

              <FaFileInvoice
                size={40}
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No VAT records
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default VATReport;